// Twitter Integration Implementation
// Provides integration with Twitter API v2 for posting and analytics

import { BaseSocialIntegration, SocialPlatform, SocialPost, SocialEngagement, SocialAudience } from '../base/BaseSocialIntegration';
import { IntegrationConfig, ConnectionResult, TestResult, ValidationResult } from '../../../types/integration';

interface TwitterCredentials {
  bearerToken: string;
  apiKey: string;
  apiSecret: string;
  accessToken: string;
  accessTokenSecret: string;
  clientId?: string;
  clientSecret?: string;
}

interface TwitterUser {
  id: string;
  name: string;
  username: string;
  profile_image_url: string;
  public_metrics: {
    followers_count: number;
    following_count: number;
    tweet_count: number;
    listed_count: number;
  };
  verified: boolean;
}

// Removed unused interfaces - TwitterTweet and TwitterMedia
// These were defined but never used in the implementation

export class TwitterIntegration extends BaseSocialIntegration {
  private credentials: TwitterCredentials;
  private baseUrl = 'https://api.twitter.com/2';
  private uploadUrl = 'https://upload.twitter.com/1.1';
  private connectedUser?: TwitterUser;

  private lastConnectedAt?: Date;
  private lastSyncAt?: Date;

  constructor(config: IntegrationConfig) {
    super(config);
    // Extract credentials from encrypted format - in real implementation would decrypt
    this.credentials = (config.credentials as unknown as TwitterCredentials);
  }

  async connect(): Promise<ConnectionResult> {
    try {
      // Verify credentials and get user profile
      const userResponse = await this.makeRequest('/users/me', {
        'user.fields': 'id,name,username,profile_image_url,public_metrics,verified'
      });

      this.connectedUser = userResponse.data;
      
      // Update connection status
      this.lastConnectedAt = new Date();

      console.log('Twitter connected:', {
        user: `@${this.connectedUser.username}`,
        followers: this.connectedUser.public_metrics.followers_count,
        verified: this.connectedUser.verified
      });

      return {
        success: true,
        message: `Connected to Twitter as @${this.connectedUser.username}`,
        connectionId: `twitter_${Date.now()}`,
        capabilities: ['post_publishing', 'analytics', 'media_upload', 'thread_posting'],
        metadata: {
          username: this.connectedUser.username,
          followers: this.connectedUser.public_metrics.followers_count,
          verified: this.connectedUser.verified
        }
      };

    } catch (error) {
      console.error('Twitter connection failed:', error instanceof Error ? error.message : 'Unknown error');
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  async disconnect(): Promise<void> {
    try {
      this.connectedUser = undefined;
      
      console.log('Twitter disconnected');
    } catch (error) {
      console.error('Twitter disconnection failed:', error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  }

  async testConnection(): Promise<TestResult> {
    const startTime = Date.now();
    try {
      const response = await this.makeRequest('/users/me', {
        'user.fields': 'id,name,username'
      });
      
      if (response.data?.id) {
        return {
          success: true,
          message: `Connected as @${response.data.username}`,
          duration: Date.now() - startTime,
          details: { 
            userId: response.data.id, 
            username: response.data.username,
            name: response.data.name
          }
        };
      }
      
      return {
        success: false,
        message: 'Invalid response from Twitter API',
        duration: Date.now() - startTime
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Connection test failed',
        duration: Date.now() - startTime
      };
    }
  }

  async validateConfig(): Promise<ValidationResult> {
    const errors: Array<{ field: string; message: string; code: string }> = [];
    const warnings: Array<{ field: string; message: string; suggestion?: string }> = [];
    
    if (!this.credentials.bearerToken && !this.credentials.accessToken) {
      errors.push({ 
        field: 'authentication', 
        message: 'Either Bearer Token or OAuth tokens (accessToken + accessTokenSecret) are required', 
        code: 'REQUIRED' 
      });
    }
    
    if (!this.credentials.apiKey) {
      errors.push({ field: 'apiKey', message: 'API Key is required', code: 'REQUIRED' });
    }
    
    if (!this.credentials.apiSecret) {
      errors.push({ field: 'apiSecret', message: 'API Secret is required', code: 'REQUIRED' });
    }
    
    if (this.credentials.accessToken && !this.credentials.accessTokenSecret) {
      errors.push({ 
        field: 'accessTokenSecret', 
        message: 'Access Token Secret is required when using OAuth 1.0a', 
        code: 'REQUIRED' 
      });
    }
    
    // Add warnings for optional fields
    if (!this.credentials.bearerToken && this.credentials.accessToken) {
      warnings.push({ 
        field: 'bearerToken', 
        message: 'Using OAuth 1.0a tokens - Bearer Token recommended for better performance',
        suggestion: 'Consider upgrading to OAuth 2.0 with Bearer Token'
      });
    }
    
    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  async getConnectedPlatforms(): Promise<SocialPlatform[]> {
    if (!this.connectedUser) {
      return [];
    }

    return [{
      id: this.connectedUser.id,
      name: 'twitter',
      displayName: `Twitter - @${this.connectedUser.username}`,
      connected: true,
      accountId: this.connectedUser.id,
      accountName: this.connectedUser.username,
      permissions: ['tweet.read', 'tweet.write', 'users.read', 'offline.access'],
      connectedAt: this.lastConnectedAt || new Date(),
      lastSyncAt: this.lastSyncAt
    }];
  }

  async connectPlatform(platform: SocialPlatform['name'], credentials: any): Promise<void> {
    if (platform === 'twitter') {
      this.credentials = { ...this.credentials, ...credentials };
      await this.connect();
    } else {
      throw new Error(`Platform ${platform} not supported by Twitter integration`);
    }
  }

  async disconnectPlatform(platform: SocialPlatform['name']): Promise<void> {
    if (platform === 'twitter') {
      await this.disconnect();
    } else {
      throw new Error(`Platform ${platform} not supported by Twitter integration`);
    }
  }

  async createPost(postData: Omit<SocialPost, 'id' | 'engagement'>): Promise<SocialPost> {
    try {
      if (postData.platform !== 'twitter') {
        throw new Error(`Platform ${postData.platform} not supported`);
      }

      const tweetData: any = {
        text: postData.content
      };

      // Handle media attachments
      if (postData.media && postData.media.length > 0) {
        const mediaIds: string[] = [];
        
        for (const media of postData.media.slice(0, 4)) { // Twitter allows max 4 media
          const mediaId = await this.uploadMedia(media.url, media.type, media.altText);
          mediaIds.push(mediaId);
        }
        
        if (mediaIds.length > 0) {
          tweetData.media = { media_ids: mediaIds };
        }
      }

      const response = await this.makeRequest('/tweets', tweetData, 'POST');

      const post: SocialPost = {
        id: response.data.id,
        platform: 'twitter',
        content: postData.content,
        media: postData.media,
        status: 'published',
        publishedTime: new Date(),
        engagement: {
          likes: 0,
          comments: 0,
          shares: 0,
          clicks: 0,
          impressions: 0,
          reach: 0,
          engagementRate: 0,
          lastUpdated: new Date()
        },
        hashtags: postData.hashtags || this.extractHashtags(postData.content),
        mentions: postData.mentions || this.extractMentions(postData.content),
        campaignId: postData.campaignId
      };

      console.log('Twitter post created:', { post });
      
      return post;
    } catch (error) {
      console.error('Twitter post creation failed:', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async updatePost(postId: string, _updates: Partial<SocialPost>): Promise<SocialPost> {
    try {
      // Twitter doesn't allow editing published tweets
      throw new Error('Twitter does not support editing published tweets');
    } catch (error) {
      console.error('Twitter post update failed:', {
        postId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async deletePost(postId: string): Promise<void> {
    try {
      await this.makeRequest(`/tweets/${postId}`, {}, 'DELETE');
      
      console.log('Twitter post deleted:', { postId });
    } catch (error) {
      console.error('Twitter post deletion failed:', {
        postId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async getPost(postId: string): Promise<SocialPost | null> {
    try {
      const response = await this.makeRequest(`/tweets/${postId}`, {
        'tweet.fields': 'id,text,created_at,author_id,public_metrics,entities,attachments',
        'user.fields': 'username'
      });

      if (!response.data) return null;

      const tweet = response.data;
      const engagement = this.parseTwitterMetrics(tweet.public_metrics);

      return {
        id: tweet.id,
        platform: 'twitter',
        content: tweet.text,
        status: 'published',
        publishedTime: new Date(tweet.created_at),
        engagement,
        hashtags: tweet.entities?.hashtags?.map((h: any) => `#${h.tag}`) || [],
        mentions: tweet.entities?.mentions?.map((m: any) => `@${m.username}`) || []
      };
    } catch (error) {
      console.error('Failed to get Twitter post:', error);
      return null;
    }
  }

  async schedulePost(postData: Omit<SocialPost, 'id' | 'engagement'>, scheduledTime: Date): Promise<SocialPost> {
    try {
      // Twitter API v2 doesn't support native scheduling
      // This would typically be handled by a scheduling service
      throw new Error('Twitter post scheduling requires third-party scheduling service or Twitter Ads API');
    } catch (error) {
      console.error('Twitter post scheduling failed:', {
        scheduledTime,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async publishPost(postId: string): Promise<SocialPost> {
    try {
      // Twitter doesn't have a draft/publish concept for regular tweets
      const post = await this.getPost(postId);
      if (!post) {
        throw new Error('Post not found');
      }

      post.status = 'published';
      post.publishedTime = new Date();

      console.log('Twitter post published:', { post });
      
      return post;
    } catch (error) {
      console.error('Twitter post publishing failed:', {
        postId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async getEngagementData(postId: string): Promise<SocialEngagement> {
    try {
      const response = await this.makeRequest(`/tweets/${postId}`, {
        'tweet.fields': 'public_metrics'
      });

      if (!response.data?.public_metrics) {
        return {
          likes: 0,
          comments: 0,
          shares: 0,
          clicks: 0,
          impressions: 0,
          reach: 0,
          engagementRate: 0,
          lastUpdated: new Date()
        };
      }

      return this.parseTwitterMetrics(response.data.public_metrics);
    } catch (error) {
      console.error('Failed to get Twitter engagement data:', error);
      return {
        likes: 0,
        comments: 0,
        shares: 0,
        clicks: 0,
        impressions: 0,
        reach: 0,
        engagementRate: 0,
        lastUpdated: new Date()
      };
    }
  }

  async createAudience(audienceData: Omit<SocialAudience, 'id'>): Promise<SocialAudience> {
    try {
      // Twitter audience creation requires Twitter Ads API
      throw new Error('Twitter audience creation requires Twitter Ads API');
    } catch (error) {
      console.error('Twitter audience creation failed:', {
        audienceName: audienceData.name,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async syncAudience(audienceId: string, _emails: string[]): Promise<void> {
    try {
      // Twitter audience sync requires Twitter Ads API
      throw new Error('Twitter audience sync requires Twitter Ads API');
    } catch (error) {
      console.error('Twitter audience sync failed:', {
        audienceId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async createLookalikeAudience(sourceAudienceId: string, similarity: number): Promise<SocialAudience> {
    try {
      // Twitter lookalike audiences require Twitter Ads API
      throw new Error('Twitter lookalike audience creation requires Twitter Ads API');
    } catch (error) {
      console.error('Twitter lookalike audience creation failed:', {
        sourceAudienceId,
        similarity,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  /**
   * Create a Twitter thread (multiple connected tweets)
   */
  async createThread(tweets: string[], media?: Array<{ url: string; type: 'image' | 'video' | 'gif'; altText?: string }>): Promise<SocialPost[]> {
    try {
      const createdTweets: SocialPost[] = [];
      let replyToId: string | undefined;

      for (let i = 0; i < tweets.length; i++) {
        const tweetData: any = {
          text: tweets[i]
        };

        // Add reply reference for subsequent tweets
        if (replyToId) {
          tweetData.reply = {
            in_reply_to_tweet_id: replyToId
          };
        }

        // Handle media for first tweet only (Twitter limitation)
        if (i === 0 && media && media.length > 0) {
          const mediaIds: string[] = [];
          
          for (const mediaItem of media.slice(0, 4)) {
            const mediaId = await this.uploadMedia(mediaItem.url, mediaItem.type, mediaItem.altText);
            mediaIds.push(mediaId);
          }
          
          if (mediaIds.length > 0) {
            tweetData.media = { media_ids: mediaIds };
          }
        }

        const response = await this.makeRequest('/tweets', tweetData, 'POST');
        
        // Convert media to SocialMedia format if present
        const socialMedia = i === 0 && media ? media.map((m, index) => ({
          id: `media_${Date.now()}_${index}`,
          type: m.type,
          url: m.url,
          altText: m.altText
        })) : undefined;

        const post: SocialPost = {
          id: response.data.id,
          platform: 'twitter',
          content: tweets[i],
          media: socialMedia,
          status: 'published',
          publishedTime: new Date(),
          engagement: {
            likes: 0,
            comments: 0,
            shares: 0,
            clicks: 0,
            impressions: 0,
            reach: 0,
            engagementRate: 0,
            lastUpdated: new Date()
          },
          hashtags: this.extractHashtags(tweets[i]),
          mentions: this.extractMentions(tweets[i])
        };

        createdTweets.push(post);
        replyToId = response.data.id;

        // Add small delay between tweets to avoid rate limiting
        if (i < tweets.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      console.log('Twitter thread created:', { 
        threadLength: createdTweets.length,
        firstTweetId: createdTweets[0].id
      });

      return createdTweets;
    } catch (error) {
      console.error('Twitter thread creation failed:', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  // Private helper methods

  private async uploadMedia(mediaUrl: string, mediaType: 'image' | 'video' | 'gif', altText?: string): Promise<string> {
    try {
      // Download media
      const mediaResponse = await fetch(mediaUrl);
      const mediaBuffer = await mediaResponse.arrayBuffer();
      
      // Determine media category - Twitter treats GIFs as videos
      const mediaCategory = (mediaType === 'video' || mediaType === 'gif') ? 'tweet_video' : 'tweet_image';
      
      // Initialize upload
      const initResponse = await this.makeUploadRequest('/media/upload.json', {
        command: 'INIT',
        total_bytes: mediaBuffer.byteLength,
        media_type: mediaResponse.headers.get('content-type') || (mediaType === 'video' ? 'video/mp4' : 'image/jpeg'),
        media_category: mediaCategory
      }, 'POST');

      const mediaId = initResponse.media_id_string;

      // Upload media in chunks (for large files)
      const chunkSize = 5 * 1024 * 1024; // 5MB chunks
      let segmentIndex = 0;
      
      for (let i = 0; i < mediaBuffer.byteLength; i += chunkSize) {
        const chunk = mediaBuffer.slice(i, Math.min(i + chunkSize, mediaBuffer.byteLength));
        
        const formData = new FormData();
        formData.append('command', 'APPEND');
        formData.append('media_id', mediaId);
        formData.append('segment_index', segmentIndex.toString());
        formData.append('media', new Blob([chunk]));

        await this.makeUploadRequest('/media/upload.json', formData, 'POST', true);
        segmentIndex++;
      }

      // Finalize upload
      await this.makeUploadRequest('/media/upload.json', {
        command: 'FINALIZE',
        media_id: mediaId
      }, 'POST');

      // Add alt text if provided
      if (altText && mediaType === 'image') {
        await this.makeUploadRequest('/media/metadata/create.json', {
          media_id: mediaId,
          alt_text: {
            text: altText
          }
        }, 'POST');
      }

      return mediaId;
    } catch (error) {
      console.error('Twitter media upload failed:', error);
      throw error;
    }
  }

  private async makeRequest(endpoint: string, data: any = {}, method: 'GET' | 'POST' | 'DELETE' = 'GET'): Promise<any> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${this.credentials.bearerToken}`,
      'Content-Type': 'application/json'
    };

    let response: Response;
    
    if (method === 'GET') {
      const params = new URLSearchParams();
      Object.keys(data).forEach(key => {
        params.append(key, data[key]);
      });
      
      const urlWithParams = params.toString() ? `${url}?${params.toString()}` : url;
      response = await fetch(urlWithParams, { method, headers });
    } else if (method === 'DELETE') {
      response = await fetch(url, { method, headers });
    } else {
      response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(data)
      });
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Twitter API error: ${response.status} - ${errorData.detail || errorData.title || response.statusText}`);
    }

    return await response.json();
  }

  private async makeUploadRequest(endpoint: string, data: any, method: 'POST', isFormData: boolean = false): Promise<any> {
    const url = `${this.uploadUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${this.credentials.bearerToken}`
    };

    let body: any;
    
    if (isFormData) {
      body = data; // FormData object
    } else if (data instanceof FormData) {
      body = data;
    } else {
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
      body = new URLSearchParams(data).toString();
    }

    const response = await fetch(url, {
      method,
      headers,
      body
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Twitter Upload API error: ${response.status} - ${errorData.error || response.statusText}`);
    }

    return await response.json();
  }

  private parseTwitterMetrics(metrics: any): SocialEngagement {
    if (!metrics) {
      return {
        likes: 0,
        comments: 0,
        shares: 0,
        clicks: 0,
        impressions: 0,
        reach: 0,
        engagementRate: 0,
        lastUpdated: new Date()
      };
    }

    const engagement: SocialEngagement = {
      likes: metrics.like_count || 0,
      comments: metrics.reply_count || 0,
      shares: (metrics.retweet_count || 0) + (metrics.quote_count || 0),
      clicks: 0, // Not available in public metrics
      impressions: metrics.impression_count || 0,
      reach: metrics.impression_count || 0, // Twitter doesn't separate reach from impressions in public metrics
      engagementRate: 0,
      lastUpdated: new Date()
    };

    // Calculate engagement rate
    if (engagement.impressions > 0) {
      const totalEngagements = engagement.likes + engagement.comments + engagement.shares;
      engagement.engagementRate = (totalEngagements / engagement.impressions) * 100;
    }

    return engagement;
  }

  private extractHashtags(content: string): string[] {
    const hashtagRegex = /#[\w]+/g;
    return content.match(hashtagRegex) || [];
  }

  private extractMentions(content: string): string[] {
    const mentionRegex = /@[\w]+/g;
    return content.match(mentionRegex) || [];
  }

  /**
   * Get the last sync time for this integration
   */
  getLastSyncTime(): Date | undefined {
    return this.lastSyncAt;
  }

  /**
   * Update the last sync time
   */
  protected updateLastSyncTime(): void {
    this.lastSyncAt = new Date();
  }
}